import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Page, PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { naira, fmtDateTime } from "../lib/format";
import { AlertTriangle, Check, X, Inbox, Clock, Calendar, Wallet, Banknote, Filter, FileText } from "lucide-react";

const TYPE_META = {
  LEAVE_REQUEST:            { label: "Leave",            icon: Calendar,    color: "#4F7A52" },
  LATENESS_CORRECTION:      { label: "Lateness",         icon: Clock,       color: "#B7791F" },
  LATENESS_PRE_NOTIFICATION:{ label: "Pre-notify late",  icon: Clock,       color: "#C49A4A" },
  DEDUCTION_WAIVER:         { label: "Deduction waiver", icon: Wallet,      color: "#8B1E1E" },
  TILL_CASH_MOVEMENT:       { label: "Cash movement",    icon: Banknote,    color: "#6B1717" },
  TILL_CASH_EXCEPTION:      { label: "Cash exception",   icon: AlertTriangle, color: "#A52222" },
  BRANCH_PROCUREMENT:       { label: "Procurement",      icon: FileText,    color: "#a58f7c" },
  REFUND:                   { label: "Refund",           icon: X,           color: "#A52222" },
  PAYROLL_RUN:              { label: "Payroll",          icon: Wallet,      color: "#6B1717" },
  SHIFT_SCHEDULE:           { label: "Roster",           icon: Calendar,    color: "#4F7A52" },
  ABSENCE_PRE_NOTIFICATION: { label: "Absence",          icon: Clock,       color: "#C49A4A" },
};

export default function ApprovalInbox() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [busyId, setBusyId] = useState(null);
  const [open, setOpen] = useState(null); // request being reviewed
  const [reason, setReason] = useState("");
  const [audit, setAudit] = useState([]);

  const load = () => {
    api.get("/approvals/requests", { params: { pending_for_me: true, status: "PENDING" } })
      .then((r) => setRows(r || []))
      .catch(() => setRows([]));
  };
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, []);

  useEffect(() => {
    if (!open) { setAudit([]); return; }
    api.get(`/approvals/requests/${open.id}/audit`).then((a) => setAudit(a || []));
  }, [open]);

  const counts = useMemo(() => {
    const m = { ALL: rows.length };
    rows.forEach((r) => { m[r.workflow_type] = (m[r.workflow_type] || 0) + 1; });
    return m;
  }, [rows]);

  const filtered = filter === "ALL" ? rows : rows.filter((r) => r.workflow_type === filter);

  const act = async (r, kind) => {
    setBusyId(r.id);
    try {
      const body = kind === "reject" ? { reason: reason || "Rejected" } : { note: reason || "" };
      await api.post(`/approvals/requests/${r.id}/${kind}`, body);
      toast.success(`Request ${kind === "approve" ? "approved" : "rejected"}`);
      setOpen(null); setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusyId(null); }
  };

  return (
    <Page>
      <PageHeader title="Approval Inbox" subtitle="Every pending HR, cash and workflow approval waiting for your action" />

      <div className="flex flex-wrap gap-2 mb-5" data-testid="inbox-filters">
        {["ALL", ...Object.keys(TYPE_META)].filter((k) => k === "ALL" || counts[k]).map((k) => {
          const meta = TYPE_META[k];
          const Icon = meta?.icon || Filter;
          return (
            <button key={k} onClick={() => setFilter(k)} data-testid={`inbox-filter-${k.toLowerCase()}`}
              className="text-xs px-3 py-2 rounded-lg font-semibold flex items-center gap-2 border transition-all"
              style={{
                background: filter === k ? "#8B1E1E" : "#fff", color: filter === k ? "#fff" : "#3B2722",
                borderColor: filter === k ? "#8B1E1E" : "#E9DED0",
              }}>
              <Icon size={13} /> {k === "ALL" ? "All" : meta.label}
              <span style={{ background: filter === k ? "rgba(255,255,255,.2)" : "#F3ECE2", padding: "1px 7px", borderRadius: 10, fontSize: 10 }}>{counts[k] || 0}</span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center" data-testid="inbox-empty">
          <Inbox size={38} className="mx-auto" style={{ color: "#C49A4A" }} />
          <div className="mt-3 font-brand font-semibold text-lg" style={{ color: "#3B2722" }}>All caught up</div>
          <div className="text-sm" style={{ color: "#a58f7c" }}>Nothing is waiting for your approval right now.</div>
        </Card>
      ) : (
        <div className="rounded-xl border shadow-sm overflow-hidden" style={{ background: "#FAF7F0", borderColor: "#ECE4D8" }} data-testid="inbox-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs uppercase" style={{ background: "#EFE7D6", color: "#8a6f5c", borderColor: "#F0E9DD" }}>
                <th className="text-left px-5 py-3">Type</th>
                <th className="text-left px-4 py-3">Subject</th>
                <th className="text-left px-4 py-3">Requester</th>
                <th className="text-left px-4 py-3">Waiting at</th>
                <th className="text-left px-4 py-3">Submitted</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const meta = TYPE_META[r.workflow_type] || { label: r.workflow_type, icon: Filter, color: "#8B1E1E" };
                const Icon = meta.icon;
                return (
                  <tr key={r.id} data-testid={`inbox-row-${i}`} className="border-b hover:bg-white/60" style={{ borderColor: "#F5EFE5" }}>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded" style={{ background: `${meta.color}12`, color: meta.color }}>
                        <Icon size={12} /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium" style={{ color: "#3B2722" }}>{r.subject}</td>
                    <td className="px-4 py-3">{r.requester_name || "—"}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: "#765A4A" }}>{r.current_level_symbol} · L{r.current_level + 1}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: "#a58f7c" }}>{fmtDateTime(r.created_at)}</td>
                    <td className="px-5 py-3 text-right space-x-2">
                      <Button variant="outline" size="sm" onClick={() => setOpen(r)} data-testid={`inbox-view-${i}`}>Review</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(59,39,34,.5)" }} data-testid="inbox-modal">
          <div className="bg-white rounded-2xl w-[560px] max-h-[85vh] flex flex-col p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <div className="font-brand font-bold text-lg" style={{ color: "#8B1E1E" }}>{open.subject}</div>
              <button onClick={() => { setOpen(null); setReason(""); }} className="text-slate-500 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm mb-3" style={{ color: "#3B2722" }}>
              <div><div className="text-xs" style={{ color: "#a58f7c" }}>Type</div><div className="font-semibold">{open.workflow_type}</div></div>
              <div><div className="text-xs" style={{ color: "#a58f7c" }}>Requester</div><div className="font-semibold">{open.requester_name}</div></div>
              <div><div className="text-xs" style={{ color: "#a58f7c" }}>Level</div><div className="font-semibold">{open.current_level_symbol} · L{open.current_level + 1}</div></div>
              {open.amount ? <div><div className="text-xs" style={{ color: "#a58f7c" }}>Amount</div><div className="font-semibold">{naira(open.amount)}</div></div> : null}
              <div className="col-span-2"><div className="text-xs" style={{ color: "#a58f7c" }}>Reason</div><div className="text-sm">{open.reason || "—"}</div></div>
            </div>
            {open.metadata && (
              <details className="mb-3">
                <summary className="text-xs cursor-pointer" style={{ color: "#a58f7c" }}>Metadata</summary>
                <pre className="text-xs mt-2 p-3 rounded bg-slate-50 overflow-x-auto">{JSON.stringify(open.metadata, null, 2)}</pre>
              </details>
            )}
            <div className="border-t pt-3 flex-1 overflow-y-auto" style={{ borderColor: "#F0E9DD" }}>
              <div className="text-xs font-semibold mb-2" style={{ color: "#8a6f5c" }}>Audit trail</div>
              {audit.length === 0 ? <div className="text-xs" style={{ color: "#a58f7c" }}>No audit entries yet.</div>
                : audit.map((a) => (
                  <div key={a.id} className="text-xs py-1 border-b" style={{ borderColor: "#F5EFE5" }}>
                    <b>{a.actor_name || "—"}</b> · {a.action} <span style={{ color: "#a58f7c" }}>· {fmtDateTime(a.at)}</span>
                    {a.meta?.note && <div style={{ color: "#765A4A" }}>"{a.meta.note}"</div>}
                  </div>
                ))}
            </div>
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Note (optional for approve, required for reject)" data-testid="inbox-note"
              className="mt-3 w-full rounded border p-2 text-sm" style={{ borderColor: "#E9DED0", background: "#FAF7F0" }} />
            <div className="flex gap-2 mt-3">
              <Button onClick={() => act(open, "reject")} disabled={busyId === open.id || !reason} variant="outline" data-testid="inbox-reject"
                className="flex-1" style={{ borderColor: "#A52222", color: "#A52222" }}>
                <X size={14} className="mr-1" /> Reject
              </Button>
              <Button onClick={() => act(open, "approve")} disabled={busyId === open.id} data-testid="inbox-approve"
                className="flex-1 text-white" style={{ background: "#4F7A52" }}>
                <Check size={14} className="mr-1" /> Approve
              </Button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
